import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useUI } from "../../contexts/ui";
import * as AskPunttService from "../../services/askPuntt";

/**
 * React Query hooks for Ask Puntt chat API
 */

// Query keys for cache management
export const askPunttKeys = {
  all: ["ask-puntt"] as const,
  chats: () => [...askPunttKeys.all, "chats"] as const,
  chat: (chatId: string) => [...askPunttKeys.all, "chat", chatId] as const,
  messages: (chatId: string) =>
    [...askPunttKeys.chat(chatId), "messages"] as const,
  streaming: (chatId: string, messageId: string) =>
    [...askPunttKeys.chat(chatId), "streaming", messageId] as const,
} as const;

/**
 * List recent Ask Puntt chats for sidebar
 */
export function useAskPunttChats(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: askPunttKeys.chats(),
    queryFn: () => AskPunttService.listChats({ limit: 20 }),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Get full chat history for a specific chat.
 * Pass refetchInterval to enable polling (e.g. while waiting for an AI response).
 */
export function useAskPunttChatHistory(
  chatId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttKeys.messages(chatId || ""),
    queryFn: () => AskPunttService.getChatHistory(chatId!),
    enabled: !!chatId && options?.enabled !== false,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Create a new Ask Puntt chat (sends first message and gets response)
 */
export function useCreateAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: AskPunttService.createChat,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chats() });
    },
    onError: (error) => {
      console.error("Failed to create Ask Puntt chat:", error);
      notify({
        title: "Failed to create chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Delete an Ask Puntt chat and invalidate the chats list.
 */
export function useDeleteAskPunttChat() {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (chatId: string) => AskPunttService.deleteChat(chatId),
    onSuccess: (_data, chatId) => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chats() });
      queryClient.removeQueries({ queryKey: askPunttKeys.chat(chatId) });
    },
    onError: (error) => {
      console.error("Failed to delete Ask Puntt chat:", error);
      notify({
        title: "Failed to delete chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Lightweight polling for a single message's status fields.
 * Returns only streaming-relevant fields via MongoDB projection.
 */
export function useAskPunttMessageStatus(
  chatId: string | null,
  messageId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: [...askPunttKeys.chat(chatId || ""), "message-status", messageId],
    queryFn: () => AskPunttService.getMessageStatus(chatId!, messageId!),
    enabled: !!chatId && !!messageId && options?.enabled !== false,
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Submit thumbs up/down feedback on an assistant message.
 * Optimistically updates the message in the query cache.
 */
export function useSubmitAskPunttFeedback(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: {
      messageId: string;
      feedback: "thumbs_up" | "thumbs_down";
    }) =>
      AskPunttService.submitFeedback(chatId, params.messageId, params.feedback),
    onMutate: async (params) => {
      const queryKey = askPunttKeys.messages(chatId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(
        queryKey,
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              m._id === params.messageId
                ? { ...m, feedback: params.feedback }
                : m,
            ),
          };
        },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          askPunttKeys.messages(chatId),
          context.previous,
        );
      }
    },
  });
}

/**
 * Send a message to an existing Ask Puntt chat.
 * Returns the saved user message and a workflowId (AI response arrives async).
 */
export function useSendAskPunttMessage(chatId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();

  return useMutation({
    mutationFn: (payload: {
      message: string;
      attachments?: {
        key: string;
        source: string;
        type: string;
        name?: string;
      }[];
      sectionContext?: { id: string; title: string };
    }) => AskPunttService.sendMessage(chatId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chats() });
    },
    onError: (error) => {
      console.error("Failed to send Ask Puntt message:", error);
      notify({
        title: "Failed to send message",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Abort an in-progress Ask Puntt message (stop generating).
 * Optimistically clears waitingForAI state; caller handles revert on error.
 */
export function useAbortAskPunttMessage(chatId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (messageId: string) =>
      AskPunttService.abortMessage(chatId, messageId),
    onSuccess: (data) => {
      // Apply the final (partial) message to the cache so polling stops naturally
      queryClient.setQueryData(
        askPunttKeys.messages(chatId),
        (
          old:
            | Awaited<ReturnType<typeof AskPunttService.getChatHistory>>
            | undefined,
        ) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              m._id === data.message._id
                ? { ...m, ...data.message, status: "completed" as const }
                : m,
            ),
          };
        },
      );
    },
  });
}

/**
 * Lightweight streaming state poll for a single message.
 * Returns only the fields needed during streaming (no full chat document).
 * Uses 500ms polling interval for faster streaming updates.
 */
export function useAskPunttStreamingState(
  chatId: string,
  messageId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttKeys.streaming(chatId, messageId || ""),
    queryFn: () => AskPunttService.getMessageStreamingState(chatId, messageId!),
    enabled: !!chatId && !!messageId && options?.enabled !== false,
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}
